import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import CrmLayout from '@/modules/crm/components/CrmLayout';
import OpportunityForm from '@/modules/crm/components/OpportunityForm';
import InteractionTimeline from '@/modules/crm/components/InteractionTimeline';
import { useCrmOpportunity, useUpdateOpportunity, useDeleteOpportunity, useAddInteraction } from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';

// Stage config
const STAGE_CONFIG = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700', icon: '🆕' },
  active: { label: 'Active', color: 'bg-orange-100 text-orange-700', icon: '💬' },
  converted: { label: 'Converted', color: 'bg-green-100 text-green-700', icon: '✅' },
  inactive: { label: 'Inactive', color: 'bg-gray-100 text-gray-500', icon: '⏸️' },
};

export default function CrmOpportunityDetailPage() {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const { toasts, showToast, hideToast } = useToast();
  
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const { data: opportunity, isLoading, error } = useCrmOpportunity(opportunityId);
  const updateMutation = useUpdateOpportunity();
  const deleteMutation = useDeleteOpportunity();
  const addInteractionMutation = useAddInteraction();

  const handleUpdate = (formData) => {
    updateMutation.mutate(
      { opportunityId, ...formData },
      {
        onSuccess: () => {
          showToast('Opportunity updated', 'success');
          setIsEditing(false);
        },
        onError: (error) => {
          showToast(error.message || 'Failed to update opportunity', 'error');
        },
      }
    );
  };

  const handleDelete = () => {
    deleteMutation.mutate(opportunityId, {
      onSuccess: () => {
        showToast('Opportunity deleted', 'success');
        navigate('/crm/opportunities');
      },
      onError: (error) => {
        showToast(error.message || 'Failed to delete opportunity', 'error');
      },
    });
  };

  const handleAddNote = (content, interactionType = 'note') => {
    addInteractionMutation.mutate(
      { 
        opportunityId, 
        contactId: opportunity.contactId,
        content,
        interactionType,
      },
      {
        onSuccess: () => {
          showToast('Interaction added', 'success');
        },
        onError: (error) => {
          showToast(error.message || 'Failed to add interaction', 'error');
        },
      }
    );
  };

  const handleStageChange = (newStage) => {
    updateMutation.mutate(
      { opportunityId, stage: newStage },
      {
        onSuccess: () => {
          showToast('Stage updated', 'success');
        },
        onError: (error) => {
          showToast(error.message || 'Failed to update stage', 'error');
        },
      }
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return `£${parseFloat(value).toLocaleString()}`;
  };

  const getContactName = () => {
    if (!opportunity) return 'Unknown';
    const parts = [opportunity.contactFirstName, opportunity.contactLastName].filter(Boolean);
    return parts.join(' ') || 'Unknown';
  };

  if (isLoading) {
    return (
      <CrmLayout>
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-48"></div>
            <div className="h-4 bg-gray-200 rounded w-32"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </CrmLayout>
    );
  }

  if (error || !opportunity) {
    return (
      <CrmLayout>
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <p className="text-gray-500 mb-4">Opportunity not found</p>
          <Link
            to="/crm/opportunities"
            className="text-zapt-purple hover:text-zapt-purple/80 font-medium"
          >
            Back to opportunities
          </Link>
        </div>
      </CrmLayout>
    );
  }

  const stageConfig = STAGE_CONFIG[opportunity.stage] || STAGE_CONFIG.new;

  return (
    <CrmLayout toasts={toasts} onCloseToast={hideToast}>
      {/* Back Button */}
      <button
        onClick={() => navigate('/crm/opportunities')}
        className="inline-flex items-center text-zapt-purple hover:text-zapt-purple/80 font-medium mb-6"
      >
        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Opportunities
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-1">{opportunity.title}</h1>
                <Link
                  to={`/crm/contacts/${opportunity.contactId}`}
                  className="text-zapt-purple hover:underline"
                >
                  {getContactName()}
                  {opportunity.contactCompany && ` at ${opportunity.contactCompany}`}
                </Link>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Edit
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                >
                  Delete
                </button>
              </div>
            </div>

            {/* Stage Selector */}
            <div className="flex items-center gap-4 mb-6">
              <span className="text-sm text-gray-600">Stage:</span>
              <select
                value={opportunity.stage}
                onChange={(e) => handleStageChange(e.target.value)}
                className={`text-sm px-3 py-1.5 rounded-lg border-0 ${stageConfig.color}`}
              >
                {Object.entries(STAGE_CONFIG).map(([value, config]) => (
                  <option key={value} value={value}>{config.label}</option>
                ))}
              </select>
              {opportunity.probability !== null && opportunity.stage !== 'converted' && opportunity.stage !== 'inactive' && (
                <span className="text-sm text-gray-500">
                  {opportunity.probability}% probability
                </span>
              )}
            </div>

            {/* Opportunity Details */}
            {isEditing ? (
              <OpportunityForm
                opportunity={opportunity}
                preselectedContactId={opportunity.contactId}
                preselectedContactName={getContactName()}
                onSubmit={handleUpdate}
                onCancel={() => setIsEditing(false)}
                isSubmitting={updateMutation.isPending}
              />
            ) : (
              <div className="space-y-6">
                {/* Description */}
                {opportunity.description && (
                  <div>
                    <p className="text-sm text-gray-500">Description</p>
                    <p className="text-gray-900">{opportunity.description}</p>
                  </div>
                )}

                {/* Values */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Estimated Values</h4>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Initial:</span>
                        <span className="font-medium">{formatCurrency(opportunity.estimatedInitialValue)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Monthly:</span>
                        <span className="font-medium">{formatCurrency(opportunity.estimatedMonthlyValue)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {opportunity.stage === 'converted' && (
                    <div className="bg-green-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-green-800 mb-2">Actual Values (Confirmed)</h4>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span className="text-green-700">Initial:</span>
                          <span className="font-medium text-green-900">
                            {formatCurrency(opportunity.actualInitialValue || opportunity.estimatedInitialValue)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-green-700">Monthly:</span>
                          <span className="font-medium text-green-900">
                            {formatCurrency(opportunity.actualMonthlyValue || opportunity.estimatedMonthlyValue)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Next Action */}
                {opportunity.nextAction && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-yellow-800">Next Action</p>
                    <p className="text-yellow-900">{opportunity.nextAction}</p>
                    {opportunity.nextActionDue && (
                      <p className={`text-sm mt-1 ${new Date(opportunity.nextActionDue) < new Date() ? 'text-red-600 font-medium' : 'text-yellow-700'}`}>
                        Due: {formatDate(opportunity.nextActionDue)}
                      </p>
                    )}
                    {opportunity.actionOwnerEmail && (
                      <p className="text-sm text-yellow-700">Owner: {opportunity.actionOwnerEmail}</p>
                    )}
                  </div>
                )}

                {/* Additional info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {opportunity.expectedCloseDate && (
                    <div>
                      <p className="text-sm text-gray-500">Expected Close Date</p>
                      <p className="font-medium">{formatDate(opportunity.expectedCloseDate)}</p>
                    </div>
                  )}
                  {opportunity.closedAt && (
                    <div>
                      <p className="text-sm text-gray-500">Closed At</p>
                      <p className="font-medium">{formatDate(opportunity.closedAt)}</p>
                    </div>
                  )}
                </div>

                {opportunity.notes && (
                  <div>
                    <p className="text-sm text-gray-500">Notes</p>
                    <p className="text-gray-900 whitespace-pre-wrap">{opportunity.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Interactions Timeline */}
          <InteractionTimeline
            interactions={opportunity.interactions || []}
            onAddNote={handleAddNote}
            isAdding={addInteractionMutation.isPending}
            opportunityId={opportunityId}
            contactId={opportunity.contactId}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Linked App */}
          {opportunity.linkedAppId && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Linked App</h3>
              <Link
                to={`/admin-portal/app/${opportunity.linkedAppId}`}
                className="flex items-center gap-3 hover:bg-gray-50 -m-2 p-2 rounded-lg"
              >
                <div className="w-8 h-8 bg-zapt-purple/10 rounded-lg flex items-center justify-center">
                  <span className="text-zapt-purple">📱</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{opportunity.linkedAppName}</p>
                  <p className="text-xs text-gray-500">{opportunity.linkedAppStatus}</p>
                </div>
              </Link>
            </div>
          )}

          {/* Contact */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Contact</h3>
            <Link
              to={`/crm/contacts/${opportunity.contactId}`}
              className="flex items-center gap-3 hover:bg-gray-50 -m-2 p-2 rounded-lg"
            >
              <div className="w-8 h-8 bg-zapt-purple/10 rounded-full flex items-center justify-center">
                <span className="text-zapt-purple text-sm font-medium">
                  {opportunity.contactFirstName?.charAt(0)?.toUpperCase() || '?'}
                </span>
              </div>
              <div>
                <p className="font-medium text-gray-900">{getContactName()}</p>
                {opportunity.contactCompany && (
                  <p className="text-xs text-gray-500">{opportunity.contactCompany}</p>
                )}
              </div>
            </Link>
          </div>

          {/* Created By */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Created By</h3>
            <p className="text-gray-900">{opportunity.createdByEmail || 'Unknown'}</p>
            <p className="text-xs text-gray-500">{formatDate(opportunity.createdAt)}</p>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Opportunity</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{opportunity.title}</strong>? This will also delete all associated interactions. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </CrmLayout>
  );
}
